const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:4998";

export async function getBacktestDefaults() {
  try {
    const response = await fetch(`${API_BASE}/api/backtest/defaults`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching backtest defaults:', error);
    // Return fallback defaults if API fails
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 1);
    return {
      status: "error",
      data: {
        default_start_date: "2022-01-01",
        default_end_date: endDate.toISOString().split('T')[0],
        last_data_max_date: null
      }
    };
  }
}

export async function postBacktest(strategy, backtestConfig, extraData = {}) {
  try {
    // Import the existing supabase instance
    const { supabase } = await import('../../supabase');

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const payload = {
      name: extraData.name || `Backtest ${new Date().toLocaleString()}`,
      strategy: strategy,
      progress_token: extraData.progressToken
    };

    if (extraData.optimizer) {
      payload.optimizer = extraData.optimizer;
    }
    if (extraData.variables) {
      payload.variables = extraData.variables;
    }

    if (extraData.backtestConfigId) {
      payload.backtest_config_id = extraData.backtestConfigId;
    }
    if (extraData.strategyId) {
      payload.strategy_id = extraData.strategyId;
    }
    if (extraData.screenId) {
      payload.screen_id = extraData.screenId;
    }

    if (backtestConfig) {
      if (backtestConfig.backtest || backtestConfig.weighting || backtestConfig.rebalance || backtestConfig.costs || backtestConfig.data_handling) {
        payload.backtest_config = backtestConfig;
      } else if (backtestConfig.start_date || backtestConfig.end_date) {
        payload.run = backtestConfig;
      }
    }

    const response = await fetch(`${API_BASE}/api/backtests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMsg = errorData.message || `HTTP error! status: ${response.status}`;

      // For tier restriction errors (403), throw immediately without fallback
      if (response.status === 403) {
        throw new Error(errorMsg);
      }

      throw new Error(errorMsg);
    }

    const data = await response.json();
    const result = data.result || data;
    if (result && typeof result === "object" && data.backtest_id) {
      result.backtest_id = data.backtest_id;
    }
    return result;

  } catch (error) {
    console.error('Backtest API error:', error);
    console.error('Error message:', error.message);

    // Re-throw tier restriction errors so they can be handled properly
    // Check for multiple indicators of tier errors to be robust
    const isTierError = error.message && (
      error.message.includes('tier') ||
      error.message.includes('Earliest allowed start date') ||
      (error.message.includes('backtest') && error.message.includes('years'))
    );

    if (isTierError) {
      console.error('Detected tier error, re-throwing:', error.message);
      throw error;
    }

    // Fallback to mock data if API fails for other reasons
    console.log('Falling back to mock data');
    return getMockBacktestResult();
  }
}

export async function getBacktestProgress(progressToken) {
  if (!progressToken) return null;

  try {
    console.debug('[ProgressPoll] fetch start', progressToken);
    const { supabase } = await import('../../supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.warn('[ProgressPoll] no active session for token', progressToken);
      throw new Error('No active session');
    }

    const response = await fetch(`${API_BASE}/api/backtests/progress/${progressToken}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      cache: 'no-store'
    });

    if (response.status === 404) {
      console.debug('[ProgressPoll] token not found yet', progressToken);
      return null;
    }

    if (!response.ok) {
      console.error('[ProgressPoll] request failed', progressToken, response.status);
      throw new Error(`Progress request failed (${response.status})`);
    }

    const payload = await response.json();
    console.debug('[ProgressPoll] payload received', progressToken, payload);
    return payload.data || null;
  } catch (error) {
    console.error('Error fetching backtest progress:', error);
    return null;
  }
}

// Mock data for development
export function getMockBacktestResult() {
  const startDate = new Date('2022-01-01');
  const endDate = new Date();
  const timeseries = [];
  const trades = [];
  
  // Generate mock timeseries data
  let portfolioValue = 100000;
  let benchmarkValue = 100000;
  let maxValue = 100000;
  let maxDrawdown = 0;
  
  for (let d = new Date(startDate); d <= endDate; d.setMonth(d.getMonth() + 1)) {
    const randomReturn = (Math.random() - 0.5) * 0.1; // ±5% monthly return
    const benchmarkReturn = (Math.random() - 0.5) * 0.08; // ±4% monthly return
    
    portfolioValue *= (1 + randomReturn);
    benchmarkValue *= (1 + benchmarkReturn);
    
    maxValue = Math.max(maxValue, portfolioValue);
    const drawdown = (maxValue - portfolioValue) / maxValue;
    maxDrawdown = Math.max(maxDrawdown, drawdown);
    
    timeseries.push({
      date: d.toISOString().split('T')[0],
      portfolio_value: Math.round(portfolioValue),
      benchmark_value: Math.round(benchmarkValue),
      drawdown: drawdown
    });
    
    // Generate some mock trades
    if (Math.random() < 0.1) { // 10% chance of trade each month
      trades.push({
        date: d.toISOString().split('T')[0],
        symbol: `STOCK${Math.floor(Math.random() * 100)}`,
        action: Math.random() > 0.5 ? 'buy' : 'sell',
        quantity: Math.floor(Math.random() * 100) + 10,
        price: Math.round(Math.random() * 100) + 50,
        fees: Math.round(Math.random() * 10),
        slippage: Math.round(Math.random() * 5),
        reason: 'PE percentile < 0.10'
      });
    }
  }
  
  const totalReturn = (portfolioValue - 100000) / 100000;
  const years = (endDate.getTime() - startDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  const annualizedReturn = Math.pow(1 + totalReturn, 1 / years) - 1;
  
  return {
    result: {
      metrics: {
        total_return: totalReturn,
        cagr: annualizedReturn,
        vol_annual: 0.15,
        sharpe: annualizedReturn / 0.15,
        sortino: annualizedReturn / 0.10,
        max_drawdown: maxDrawdown,
        benchmark: '^SP500TR'
      },
      equity_curve: timeseries.map(t => ({
        date: t.date,
        equity: t.portfolio_value
      })),
      benchmark_curve: timeseries.map(t => ({
        date: t.date,
        equity: t.benchmark_value
      })),
      drawdown_curve: timeseries.map(t => ({
        date: t.date,
        drawdown: -t.drawdown,
        drawdown_mag: t.drawdown
      })),
      daily_returns: timeseries.map((t, i) => ({
        date: t.date,
        ret: i === 0 ? 0 : (t.portfolio_value - timeseries[i-1].portfolio_value) / timeseries[i-1].portfolio_value
      })),
      trades: trades,
      trades_roundtrip: trades,
      trade_metrics: {
        total_trades: trades.length,
        win_rate: 0.65,
        avg_trade_return: totalReturn / trades.length,
        profit_factor_trades: 1.2,
        avg_holding_days: 30
      },
      explain: [],
      warnings: ['Using mock data for development'],
      defaults_applied: ['weighting=equal', 'costs=2/5 bps', 'rebalance=Monthly']
    }
  };
}
